/**
 * Section reach: records how far down a page each visitor actually scrolled,
 * one event per tracked section that entered the viewport.
 *
 * Mark a section by adding `data-section="<key>"` to its outermost element —
 * see home/index and home/_landing_hero. Every section on the landing page is
 * marked, but only the keys in TRACKED_SECTIONS below are recorded, because
 * each tracked section costs a row in ahoy_events on every pageview that
 * reaches it. Widening the tracking is a one-line change here; nothing in the
 * templates or the Blazer queries needs to change with it.
 *
 * Two events per pageview shape the numbers:
 *
 *   Sections Rendered  {page, sections: [...]}  once, listing what was in the DOM
 *   Section Viewed     {page, section}          once per section reached
 *
 * The first is the denominator, and it is the reason a section can be A/B
 * tested away without corrupting anything. Counting against ahoy's `$view`
 * instead would leave every pageview in the denominator whether or not the
 * section was on the page, so a section hidden by a test would read as 0%
 * reach — "nobody scrolled there" — when the truth is "it wasn't there to
 * scroll to". Those two need to stay distinguishable, and recording what was
 * actually rendered is what keeps them apart without a code change each time
 * the page changes.
 *
 * Both events are filtered through TRACKED_SECTIONS for that same reason: an
 * untracked section left in the rendered list would sit in the denominator
 * while never being able to produce a Section Viewed, and would read as a
 * section nobody ever scrolls to rather than one nobody measures.
 *
 * A section counts as reached on its first pixel entering the viewport, once
 * per pageview. This is scroll depth, not attention: someone who scrolls
 * straight past to the footer has reached everything on the way.
 *
 * Turbo is not installed (the `data-turbo-track` in the layouts is vestigial),
 * so every navigation is a full load and this runs once per pageview, exactly
 * as ahoy's own trackView does.
 */
import ahoy from "./ahoy";

// The sections recorded today. Everything else on the page is marked but
// ignored, which holds this down to one extra row per pageview plus one per
// tracked section reached. Add a key to start tracking that section.
const TRACKED_SECTIONS = new Set(["pricing"]);

function init(): void {
  const elements = Array.from(
    document.querySelectorAll<HTMLElement>("[data-section]")
  ).filter((el) => el.dataset.section && TRACKED_SECTIONS.has(el.dataset.section));

  // Every page with no tracked section on it, which is most of them.
  if (elements.length === 0) return;

  const page = window.location.pathname;

  // Distinct keys, in DOM order. A key can legitimately appear on two elements:
  // home/index renders home/_landing_howitworks in either of two slots
  // depending on bluetag_with_shipping, and under one variant that is a second
  // copy of a section already counted. One section, one entry, either way.
  const keys = Array.from(new Set(elements.map((el) => el.dataset.section!)));

  ahoy.track("Sections Rendered", { page, sections: keys });

  const pending = new Set(keys);

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const section = (entry.target as HTMLElement).dataset.section;

      // Already recorded — a second copy of the same section, or the same one
      // scrolled back past. Either way the visitor reached it once.
      if (!section || !pending.has(section)) return;

      pending.delete(section);
      ahoy.track("Section Viewed", { page, section });

      if (pending.size === 0) observer.disconnect();
    });
  });

  // Sections already on screen at load are reported by the observer's first
  // callback, so that case needs no separate check.
  elements.forEach((el) => observer.observe(el));
}

document.addEventListener("DOMContentLoaded", init);
